import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { CalendarOff, Loader2, Plane } from 'lucide-react';
import Modal from '../ui/Modal';

export interface CloseDayStaff {
    id: string;
    name: string;
    color: string;
    avatar: string;
    image?: string;
    /** Giornata già chiusa (ferie di un solo giorno su questa data) */
    isClosed: boolean;
    /** Dentro un periodo di ferie più lungo: si gestisce dalla scheda Staff */
    isLocked: boolean;
    /** Appuntamenti confermati in giornata */
    bookingsCount: number;
}

interface CloseDayModalProps {
    isOpen: boolean;
    onClose: () => void;
    date: Date;
    staff: CloseDayStaff[];
    onConfirm: (staffId: string) => void;
    isLoading?: boolean;
}

/**
 * Chiusura/riapertura della giornata di un dipendente: si sceglie il dipendente
 * dalla lista e si conferma, senza toccare l'agenda.
 */
const CloseDayModal: React.FC<CloseDayModalProps> = ({ isOpen, onClose, date, staff, onConfirm, isLoading = false }) => {
    const { t } = useTranslation();
    const [selectedId, setSelectedId] = useState<string | null>(null);

    useEffect(() => {
        if (isOpen) setSelectedId(null);
    }, [isOpen]);

    if (!isOpen) return null;

    const selected = staff.find((s) => s.id === selectedId) || null;
    const isReopen = !!selected?.isClosed;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            className="max-w-md"
            headerClassName="bg-white border-b border-gray-100 pb-4"
            closeButtonClassName="bg-gray-100 hover:bg-gray-200 text-gray-500"
            headerContent={
                <div>
                    <div className="flex items-center gap-2 mb-1">
                        <span className="bg-blue-50 text-blue-600 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider border border-blue-100 flex items-center gap-1">
                            <CalendarOff size={12} /> {t('calendar.toolbar.editday', 'Chiudi giornata')}
                        </span>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 leading-tight capitalize">
                        {format(date, 'EEEE d MMMM', { locale: it })}
                    </h2>
                </div>
            }
        >
            <div className="space-y-5">
                <p className="text-gray-600 text-sm">
                    {t('calendar.editday.modal_desc', 'Scegli il dipendente di cui chiudere (o riaprire) la giornata. A giornata chiusa i clienti non possono più prenotare.')}
                </p>

                <div className="space-y-2">
                    {staff.length === 0 && (
                        <p className="text-sm text-gray-400 text-center py-6">
                            {t('calendar.editday.no_staff', 'Nessun dipendente disponibile.')}
                        </p>
                    )}
                    {staff.map((s) => {
                        const isSelected = selectedId === s.id;
                        return (
                            <label
                                key={s.id}
                                className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${s.isLocked
                                    ? 'border-gray-100 bg-gray-50 opacity-60 cursor-not-allowed'
                                    : isSelected
                                        ? 'border-clip-500 bg-clip-50 cursor-pointer'
                                        : 'border-gray-200 hover:border-clip-200 bg-white cursor-pointer'}`}
                            >
                                <input
                                    type="radio"
                                    name="closeDayStaff"
                                    className="hidden"
                                    checked={isSelected}
                                    disabled={s.isLocked || isLoading}
                                    onChange={() => setSelectedId(s.id)}
                                />
                                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? 'border-clip-500' : 'border-gray-300'}`}>
                                    {isSelected && <div className="w-2.5 h-2.5 bg-clip-500 rounded-full" />}
                                </div>

                                <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold overflow-hidden ${s.color}`}>
                                    {s.image ? <img src={s.image} alt={s.name} className="w-full h-full object-cover" /> : s.avatar}
                                </div>

                                <div className="min-w-0 flex-1">
                                    <span className={`block font-bold text-sm truncate ${isSelected ? 'text-clip-700' : 'text-gray-800'}`}>
                                        {s.name}
                                    </span>
                                    <span className="block text-xs text-gray-500">
                                        {s.bookingsCount > 0
                                            ? t('calendar.editday.bookings_count', '{{count}} appuntamenti confermati', { count: s.bookingsCount })
                                            : t('calendar.editday.no_bookings', 'Nessun appuntamento')}
                                    </span>
                                </div>

                                {s.isLocked ? (
                                    <span className="flex-shrink-0 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-gray-200 text-gray-600 px-2 py-1 rounded-full">
                                        <Plane size={11} /> {t('calendar.editday.on_holiday', 'In ferie')}
                                    </span>
                                ) : s.isClosed ? (
                                    <span className="flex-shrink-0 text-[10px] font-bold uppercase tracking-wider bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                                        {t('calendar.editday.closed_badge', 'Chiuso')}
                                    </span>
                                ) : null}
                            </label>
                        );
                    })}
                </div>

                {selected?.isLocked && (
                    <p className="text-xs text-gray-500">
                        {t('calendar.editday.long_holiday', 'Il dipendente è in ferie: gestiscile dalla scheda Staff.')}
                    </p>
                )}

                <div className="pt-5 border-t border-gray-100 flex gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isLoading}
                        className="flex-1 py-3.5 rounded-xl font-bold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors disabled:opacity-70"
                    >
                        {t('common.cancel', 'Annulla')}
                    </button>
                    <button
                        type="button"
                        onClick={() => selected && onConfirm(selected.id)}
                        disabled={isLoading || !selected || selected.isLocked}
                        className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-xl text-white font-bold bg-clip-500 hover:bg-clip-600 shadow-lg shadow-clip-200 transition-all disabled:opacity-50 disabled:shadow-none"
                    >
                        {isLoading && <Loader2 size={18} className="animate-spin" />}
                        {isReopen
                            ? t('calendar.editday.reopen_btn', 'Riapri giornata')
                            : t('calendar.editday.close_btn', 'Chiudi giornata')}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default CloseDayModal;
